/**
 * DataGrid Component
 *
 * Displays raw statistical data in a modal table overlay.
 */

// Inject global styles once
let stylesInjected = false;

function injectGlobalStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;

  const style = document.createElement("style");
  style.setAttribute("data-gralobe-datagrid", "true");
  style.textContent = `
    .gralobe-datagrid-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 5, 10, 0.85);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 80;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.3s ease;
    }

    .gralobe-datagrid-overlay.visible {
      opacity: 1;
      pointer-events: auto;
    }

    .gralobe-datagrid-modal {
      width: 90%;
      height: 80%;
      max-width: 600px;
      background: #0d1117;
      border: 1px solid rgba(100, 150, 200, 0.3);
      border-radius: 12px;
      display: flex;
      flex-direction: column;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.6);
      transform: scale(0.95) translateY(10px);
      transition: transform 0.3s ease;
    }

    .gralobe-datagrid-overlay.visible .gralobe-datagrid-modal {
      transform: scale(1) translateY(0);
    }

    .gralobe-datagrid-header {
      padding: 16px 20px;
      border-bottom: 1px solid rgba(100, 150, 200, 0.2);
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .gralobe-datagrid-title {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #4af;
      font-family: inherit;
    }

    .gralobe-datagrid-close {
      background: none;
      border: none;
      color: #666;
      font-size: 24px;
      cursor: pointer;
      padding: 0;
      line-height: 1;
      transition: color 0.2s ease;
    }

    .gralobe-datagrid-close:hover {
      color: #fff;
    }

    .gralobe-datagrid-body {
      flex: 1;
      overflow-y: auto;
      padding: 0;
    }

    .gralobe-datagrid-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
      color: #ccc;
      font-family: inherit;
    }

    .gralobe-datagrid-table th {
      position: sticky;
      top: 0;
      background: #161b22;
      text-align: left;
      padding: 12px 20px;
      font-weight: 600;
      color: #888;
      border-bottom: 1px solid rgba(100, 150, 200, 0.15);
      z-index: 1;
    }

    .gralobe-datagrid-table td {
      padding: 10px 20px;
      border-bottom: 1px solid rgba(100, 150, 200, 0.05);
    }

    .gralobe-datagrid-table tr:hover {
      background: rgba(100, 150, 200, 0.05);
    }

    .gralobe-datagrid-val {
      text-align: right;
      font-family: monospace;
      color: #fff;
    }

    .gralobe-datagrid-name {
      font-weight: 500;
    }
  `;
  document.head.appendChild(style);
}

export class DataGrid {
  private element: HTMLElement;
  private parentContainer: HTMLElement;
  private visible: boolean = false;

  constructor(parentContainer: HTMLElement) {
    injectGlobalStyles();
    this.parentContainer = parentContainer;

    this.element = document.createElement("div");
    this.element.className = "gralobe-datagrid-overlay";
    this.element.innerHTML = `
      <div class="gralobe-datagrid-modal">
        <div class="gralobe-datagrid-header">
          <h3 class="gralobe-datagrid-title">Data Explorer</h3>
          <button class="gralobe-datagrid-close">&times;</button>
        </div>
        <div class="gralobe-datagrid-body">
          <table class="gralobe-datagrid-table">
            <thead>
              <tr>
                <th>Location</th>
                <th style="text-align: right">Value</th>
              </tr>
            </thead>
            <tbody></tbody>
          </table>
        </div>
      </div>
    `;

    const closeBtn = this.element.querySelector(".gralobe-datagrid-close");
    closeBtn?.addEventListener("click", () => this.hide());

    this.element.addEventListener("click", (e) => {
      if (e.target === this.element) this.hide();
    });

    parentContainer.appendChild(this.element);
  }

  show(title: string, data: Record<string, number>): void {
    const titleEl = this.element.querySelector(".gralobe-datagrid-title");
    if (titleEl) titleEl.textContent = `Data: ${title}`;

    const tbody = this.element.querySelector("tbody");
    if (tbody) {
      const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
      tbody.innerHTML = entries
        .map(
          ([name, val]) => `
          <tr>
            <td class="gralobe-datagrid-name">${name}</td>
            <td class="gralobe-datagrid-val">${val.toLocaleString()}</td>
          </tr>
        `,
        )
        .join("");
    }

    this.element.classList.add("visible");
    this.visible = true;
  }

  hide(): void {
    this.element.classList.remove("visible");
    this.visible = false;
  }

  dispose(): void {
    this.element.remove();
  }
}
